import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Search, Plus, Edit, Trash2, Eye, EyeOff, ArrowLeft, Menu, X, Mail } from 'lucide-react';
import './App.css';
import './ManageUsers.css';
import AppSidebar from './components/AppSidebar';
import NotificationBell from './components/NotificationBell';
import { logout as endSession } from './auth';
import { validateUserFields, validateEmail } from './utils/validation';

const API_ROOT = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_URL || '')
  : (process.env.REACT_APP_API_URL || 'http://localhost:5001');
axios.defaults.baseURL = API_ROOT;

export default function ManageUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [focused, setFocused] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const [editing, setEditing] = useState(null); // { id, username, role, password, showPwd }
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '', role: 'architect', showPwd: false });
  const [emailStatus, setEmailStatus] = useState({ state: 'idle', message: '' }); // idle | checking | valid | invalid

  // Ensure axios Authorization header
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }, []);

  // Guard: only admin
  useEffect(() => {
    if (localStorage.getItem('role') !== 'admin') {
      toast.error('Forbidden: admin only');
      window.location.replace('/dashboard');
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/users');
      setUsers(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const startEdit = (u) => setEditing({ id: u.id, username: u.username, email: u.email || '', role: u.role, password: '', showPwd: false });
  const cancelEdit = () => setEditing(null);

  const saveEdit = async () => {
    if (!editing) return;
    const fieldError = validateUserFields({
      username: editing.username,
      email: editing.email,
      password: editing.password,
      passwordOptional: true
    });
    if (fieldError) {
      toast.error(fieldError);
      return;
    }
    try {
      setLoading(true);
      const payload = { username: editing.username.trim(), email: editing.email.trim(), role: editing.role };
      if (editing.password) payload.password = editing.password;
      await axios.put(`/api/users/${editing.id}`, payload);
      toast.success('User updated successfully');
      setEditing(null);
      await loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Update failed');
    } finally { setLoading(false); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user "${u.username}"? This cannot be undone.`)) return;
    try {
      setLoading(true);
      await axios.delete(`/api/users/${u.id}`);
      toast.success('User deleted successfully');
      await loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Delete failed');
    } finally { setLoading(false); }
  };

  const resendVerification = async (u) => {
    try {
      setLoading(true);
      const res = await axios.post(`/api/users/${u.id}/resend-verification`);
      toast.success(res.data.message || 'Verification link sent');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to resend verification');
    } finally {
      setLoading(false);
    }
  };

  const closeCreateModal = () => {
    setCreating(false);
    setNewUser({ username: '', email: '', password: '', role: 'architect', showPwd: false });
    setEmailStatus({ state: 'idle', message: '' });
  };

  const verifyCreateEmail = async (rawEmail) => {
    const formatError = validateEmail(rawEmail);
    if (formatError) {
      setEmailStatus({ state: 'invalid', message: formatError });
      return { ok: false, error: formatError };
    }
    try {
      setEmailStatus({ state: 'checking', message: 'Verifying email domain…' });
      await axios.post('/api/users/verify-email', { email: rawEmail.trim() });
      setEmailStatus({ state: 'valid', message: 'Email looks valid' });
      return { ok: true };
    } catch (e) {
      const msg = e?.response?.data?.error || 'Email is not valid';
      setEmailStatus({ state: 'invalid', message: msg });
      return { ok: false, error: msg };
    }
  };

  const createUser = async (e) => {
    e?.preventDefault?.();
    const fieldError = validateUserFields({
      username: newUser.username,
      email: newUser.email,
      password: newUser.password
    });
    if (fieldError) {
      toast.error(fieldError);
      if (fieldError.toLowerCase().includes('email')) {
        setEmailStatus({ state: 'invalid', message: fieldError });
      }
      return;
    }
    try {
      setLoading(true);
      const emailCheck = await verifyCreateEmail(newUser.email);
      if (!emailCheck.ok) {
        toast.error(emailCheck.error || 'Please enter a valid email address');
        return;
      }
      const res = await axios.post('/api/users', {
        username: newUser.username.trim(),
        password: newUser.password,
        email: newUser.email.trim(),
        role: newUser.role
      });
      toast.success(res.data.message || 'User created. Login credentials sent to their email.');
      if (res.data.warning) toast.warning(res.data.warning);
      closeCreateModal();
      await loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Create failed');
    } finally { setLoading(false); }
  };

  const sortedUsers = useMemo(() => [...users].sort((a,b) => Number(a.id) - Number(b.id)), [users]);

  const sequentialUsers = useMemo(
    () => sortedUsers.map((u, index) => ({ ...u, displayId: index + 1 })),
    [sortedUsers]
  );

  const filteredUsers = useMemo(() => sequentialUsers.filter(u =>
    u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(u.displayId).includes(searchTerm)
  ), [sequentialUsers, searchTerm]);
  const suggestions = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return [];
    const base = sequentialUsers
      .map(u => ({ id: u.id, displayId: u.displayId, username: u.username, email: u.email, role: u.role }))
      .filter(u => String(u.displayId).includes(q) || u.username.toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) || u.role.toLowerCase().includes(q));
    const seen = new Set();
    const out = [];
    for (const item of base) {
      const key = `${item.id}|${item.username}`;
      if (!seen.has(key)) {
        seen.add(key);
        out.push(item);
        if (out.length >= 8) break;
      }
    }
    return out;
  }, [sequentialUsers, searchTerm]);


  const logout = () => {
    endSession();
  };

  return (
    <div className="App manage-users-page">
      <ToastContainer theme="light" position="top-right" autoClose={3000} />
      <div className="app-shell">
        <AppSidebar
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          active="users"
          onLogout={logout}
        />
        <div className="app-main">
          <header className="app-topbar">
            <div className="app-topbar-actions">
              <button
                type="button"
                className="btn btn-outline sidebar-toggle"
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
              >
                <Menu size={18} />
                Menu
              </button>
              <h1 className="app-topbar-title">User Management</h1>
            </div>
            <div className="app-topbar-actions">
              <NotificationBell />
              <button type="button" className="btn btn-outline" onClick={() => window.location.href = '/dashboard'}>
                <ArrowLeft size={16} /> Back to Dashboard
              </button>
            </div>
          </header>
          <div className="page-content">
        <div className="content-card">
        <div className="toolbar">
          <div className="search-input" onFocus={() => setFocused(true)} onBlur={() => setTimeout(() => setFocused(false), 150)}>
            <Search size={18} className="search-icon" />
            <input
              className="form-control"
              placeholder="Search users by ID, username, email, or role..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            {focused && suggestions.length > 0 && (
              <div className="suggestions-list">
                {suggestions.map(s => (
                  <div key={`${s.id}-${s.username}`} className="suggestion" onMouseDown={() => setSearchTerm(String(s.displayId))}>
                    <span className="s-id">#{s.displayId}</span>
                    <span className="s-name">{s.username}</span>
                    <span className="s-role">{s.role}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button className="btn btn-success" onClick={() => setCreating(true)}>
            <Plus size={16} /> Add User
          </button>
        </div>

        <div className="list-meta">
          Showing {filteredUsers.length} of {users.length} users
        </div>

        <div className={`table-wrapper${loading ? ' is-loading' : ''}`}>
          {loading && (
            <div className="table-loading-overlay" aria-live="polite" aria-busy="true">
              <div className="table-loading-spinner" />
              <span>Loading…</span>
            </div>
          )}
          <table className="table">
            <colgroup>
              <col style={{ width: '64px' }} />
              <col style={{ width: '140px' }} />
              <col />
              <col style={{ width: '110px' }} />
              <col style={{ width: '150px' }} />
              <col style={{ width: '220px' }} />
              <col style={{ width: '110px' }} />
              <col style={{ width: '240px' }} />
            </colgroup>
            <thead>
              <tr>
                <th>ID</th>
                <th>Username</th>
                <th>Email</th>
                <th>Status</th>
                <th>Role</th>
                <th>Password</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 && !loading ? (
                <tr>
                  <td colSpan={8} className="table-empty-cell">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map(u => (
                <tr key={u.id} className={editing?.id === u.id ? 'is-editing' : undefined}>
                  <td data-label="ID">{u.displayId}</td>
                  <td data-label="Username">
                    {editing?.id === u.id ? (
                      <input
                        className="form-control edit-input"
                        value={editing.username}
                        onChange={e => setEditing({ ...editing, username: e.target.value.replace(/\s/g, '') })}
                        minLength={3}
                        maxLength={50}
                      />
                    ) : (
                      <span className="username-cell">{u.username}</span>
                    )}
                  </td>
                  <td data-label="Email">
                    {editing?.id === u.id ? (
                      <input
                        type="email"
                        className="form-control edit-input"
                        value={editing.email}
                        onChange={e => setEditing({ ...editing, email: e.target.value })}
                        maxLength={255}
                      />
                    ) : (
                      <span className="email-cell">{u.email || '—'}</span>
                    )}
                  </td>
                  <td data-label="Status">
                    {!u.email ? (
                      <span className="text-muted text-sm">No email</span>
                    ) : Number(u.email_verified) ? (
                      <span className="badge badge-success">Verified</span>
                    ) : (
                      <span className="badge badge-warning">Unverified</span>
                    )}
                  </td>
                  <td data-label="Role">
                    {editing?.id === u.id ? (
                      <select
                        className="form-control edit-select"
                        value={editing.role}
                        onChange={e => setEditing({ ...editing, role: e.target.value })}
                      >
                        <option value="architect">Architect</option>
                        <option value="reviewer">Reviewer</option>
                        <option value="admin">Admin</option>
                      </select>
                    ) : (
                      <span className={`badge ${u.role === 'admin' ? 'badge-danger' : u.role === 'reviewer' ? 'badge-warning' : 'badge-primary'}`}>{u.role}</span>
                    )}
                  </td>
                  <td data-label="Password">
                    {editing?.id === u.id ? (
                      <div className="password-row">
                        <input
                          type={editing.showPwd ? 'text' : 'password'}
                          className="form-control edit-input"
                          value={editing.password}
                          onChange={e => setEditing({ ...editing, password: e.target.value.replace(/\s/g, '') })}
                          placeholder="New password (optional)"
                          minLength={6}
                          maxLength={128}
                        />
                        <button type="button" className="btn btn-outline" onClick={() => setEditing({ ...editing, showPwd: !editing.showPwd })}>
                          {editing.showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    ) : (
                      <span className="text-muted text-sm">••••••••</span>
                    )}
                  </td>
                  <td data-label="Created" className="text-muted text-sm">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="actions">
                    {editing?.id === u.id ? (
                      <>
                        <button className="btn btn-success" onClick={saveEdit} disabled={loading}>Save</button>
                        <button className="btn btn-secondary" onClick={cancelEdit} disabled={loading}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button className="btn btn-primary" onClick={() => startEdit(u)} disabled={loading}><Edit size={14} /> Edit</button>
                        {u.email && !Number(u.email_verified) && (
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => resendVerification(u)}
                            disabled={loading}
                            title="Resend verification email"
                          >
                            <Mail size={14} /> Verify
                          </button>
                        )}
                        <button className="btn btn-danger" onClick={() => deleteUser(u)} disabled={loading || String(u.id) === String(localStorage.getItem('userId') || '')}>
                          <Trash2 size={14} /> Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
              )}
            </tbody>
          </table>
        </div>
        </div>
      </div>
        </div>
      </div>

      {creating && (
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-user-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeCreateModal();
          }}
        >
          <div className="modal-content create-user-modal">
            <div className="modal-header">
              <h2 id="create-user-title" className="modal-title">Create New User</h2>
              <button type="button" className="close-btn" onClick={closeCreateModal} aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={createUser} className="modal-form" autoComplete="off">
              <div className="form-group">
                <label className="form-label" htmlFor="new-username">Username *</label>
                <input
                  id="new-username"
                  className="form-control"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value.replace(/\s/g, '') })}
                  placeholder="Enter username"
                  minLength={3}
                  maxLength={50}
                  pattern="[a-zA-Z0-9._-]{3,50}"
                  title="3–50 characters: letters, numbers, dots, hyphens, underscores"
                  required
                />
                <small className="field-hint">3–50 characters · letters, numbers, . _ -</small>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="new-email">Email *</label>
                <input
                  id="new-email"
                  type="email"
                  className="form-control"
                  value={newUser.email}
                  onChange={(e) => {
                    setNewUser({ ...newUser, email: e.target.value.trimStart() });
                    setEmailStatus({ state: 'idle', message: '' });
                  }}
                  onBlur={() => {
                    if (newUser.email.trim()) verifyCreateEmail(newUser.email);
                  }}
                  placeholder="Enter email address"
                  maxLength={255}
                  required
                />
                {emailStatus.state === 'checking' && (
                  <small className="field-hint">{emailStatus.message}</small>
                )}
                {emailStatus.state === 'valid' && (
                  <small className="field-hint field-hint--ok">{emailStatus.message}</small>
                )}
                {emailStatus.state === 'invalid' && (
                  <small className="field-error">{emailStatus.message}</small>
                )}
                {emailStatus.state === 'idle' && (
                  <small className="field-hint">We’ll verify the email format and domain</small>
                )}
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="new-password">Password *</label>
                <div className="password-row">
                  <input
                    id="new-password"
                    type={newUser.showPwd ? 'text' : 'password'}
                    className="form-control"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value.replace(/\s/g, '') })}
                    placeholder="Enter password"
                    minLength={6}
                    maxLength={128}
                    required
                  />
                  <button
                    type="button"
                    className="btn btn-outline"
                    aria-label={newUser.showPwd ? 'Hide password' : 'Show password'}
                    onClick={() => setNewUser({ ...newUser, showPwd: !newUser.showPwd })}
                  >
                    {newUser.showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <small className="field-hint">At least 6 characters · no spaces</small>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="new-role">Role *</label>
                <select
                  id="new-role"
                  className="form-control"
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                >
                    <option value="architect">Architect</option>
                    <option value="reviewer">Reviewer</option>
                    <option value="admin">Admin</option>
                </select>
              </div>
              <div className="modal-actions" style={{ margin: '8px -24px -24px', padding: '16px 24px' }}>
                <button type="button" className="btn btn-secondary" onClick={closeCreateModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-success" disabled={loading}>
                  {loading ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

